'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { ensureUserProfile } from '@/lib/ensureUserProfile';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  user_type: 'professional' | 'client';
  profession: string;
  onboarding_completed: boolean;
  avatar_url: string | null;
  paystack_customer_id: string | null;
  theme_preference: string | null;
}

interface AuthContextValue {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  changePassword: (newPassword: string) => Promise<any>;
  resendVerificationEmail: (emailAddress?: string) => Promise<any>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const loadProfile = useCallback(async (userId: string, authUser?: any): Promise<UserProfile | null> => {
    try {
      if (authUser) {
        await ensureUserProfile(supabase, authUser);
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.warn('[loadProfile] query warning:', error.message);
      }

      if (data) {
        setProfile(data);
        // Apply saved theme preference
        if (data.theme_preference && typeof window !== 'undefined') {
          const stored = localStorage.getItem('aven-theme');
          if (!stored) {
            localStorage.setItem('aven-theme', data.theme_preference);
          }
        }
        return data;
      }
      return null;
    } catch (err) {
      console.warn('[loadProfile] non-blocking error:', err);
      return null;
    }
  }, [supabase]);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id, session.user).finally(() => {
          if (isMounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id, session.user).finally(() => {
          if (isMounted) setLoading(false);
        });
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, loadProfile]);

  const refreshProfile = async () => {
    if (user) {
      return await loadProfile(user.id, user);
    }
    return null;
  };

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const normalizedEmail = email.trim().toLowerCase();

    // First try the server-side signup proxy (/api/auth/signup)
    // Server-side signup is same-origin, completely immune to cross-origin blocks,
    // ad-blockers (e.g. uBlock blocking *.supabase.co), and iframe sandbox issues.
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          password,
          fullName: metadata?.fullName || '',
          userType: metadata?.userType || metadata?.role || 'professional',
          profession: metadata?.profession || (metadata?.userType === 'client' ? 'client' : 'lash'),
          role: metadata?.role || metadata?.userType || 'professional',
        }),
      });
      const resData = await res.json();

      if (res.ok && resData.success) {
        if (resData.session?.access_token && resData.session?.refresh_token) {
          try {
            await supabase.auth.setSession({
              access_token: resData.session.access_token,
              refresh_token: resData.session.refresh_token,
            });
          } catch (syncErr) {
            console.warn('[signUp] client setSession sync warning:', syncErr);
          }
        }
        if (resData.user) {
          setUser(resData.user);
          setSession(resData.session);
          try {
            await ensureUserProfile(supabase, resData.user);
            await loadProfile(resData.user.id, resData.user);
          } catch (err) {
            console.warn('[signUp] ensureUserProfile non-blocking:', err);
          }
        }
        return { user: resData.user, session: resData.session };
      } else if (resData?.error === 'email_exists') {
        const err = new Error('An account with this email already exists. Please sign in instead.');
        (err as any).code = 'email_exists';
        throw err;
      } else if (resData?.message) {
        const err = new Error(resData.message);
        (err as any).code = resData.error || 'signup_error';
        throw err;
      }
    } catch (proxyErr: any) {
      if (proxyErr?.code === 'email_exists' || proxyErr?.code === 'signup_error') {
        throw proxyErr;
      }
      console.warn('[signUp] /api/auth/signup proxy fallback to direct client signup:', proxyErr);
    }

    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : process.env.NEXT_PUBLIC_SITE_URL || 'https://avenbeautylink.com';

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          role: metadata?.role || metadata?.userType || 'professional',
          profession: metadata?.profession || (metadata?.userType === 'client' ? 'client' : 'lash'),
          user_type: metadata?.userType || 'professional',
        },
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });

    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('already') || msg.includes('registered') || msg.includes('exists')) {
        const err = new Error('An account with this email already exists. Please sign in instead.');
        (err as any).code = 'email_exists';
        throw err;
      }
      throw error;
    }

    if (
      data?.user &&
      Array.isArray((data.user as any).identities) &&
      (data.user as any).identities.length === 0
    ) {
      const err = new Error('An account with this email already exists. Please sign in instead.');
      (err as any).code = 'email_exists';
      throw err;
    }

    // If user and session were immediately created (e.g. email confirmation off), ensure profile
    if (data?.user) {
      try {
        await ensureUserProfile(supabase, data.user);
        await loadProfile(data.user.id, data.user);
      } catch (err) {
        console.warn('[signUp] ensureUserProfile non-blocking:', err);
      }
    }

    return data;
  };

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    // First try the robust server-side proxy route (/api/auth/login)
    // Server-side proxy is same-origin, completely immune to cross-origin blocks,
    // ad-blockers (e.g. uBlock blocking *.supabase.co), and iframe sandbox issues.
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });
      const resData = await res.json();

      if (res.ok && resData.success) {
        if (resData.session?.access_token && resData.session?.refresh_token) {
          try {
            await supabase.auth.setSession({
              access_token: resData.session.access_token,
              refresh_token: resData.session.refresh_token,
            });
          } catch (syncErr) {
            console.warn('[signIn] client setSession sync warning:', syncErr);
          }
        }
        setUser(resData.user);
        setSession(resData.session);
        let resolvedProfile = resData.profile;
        if (resData.profile) {
          setProfile(resData.profile);
        } else if (resData.user) {
          resolvedProfile = await loadProfile(resData.user.id, resData.user);
        }
        return { user: resData.user, session: resData.session, profile: resolvedProfile };
      } else if (res.status === 401 || res.status === 400 || res.status === 404) {
        const err = new Error(resData.message || 'Invalid email or password.');
        (err as any).code = resData.error || 'invalid_credentials';
        throw err;
      }
    } catch (proxyErr: any) {
      if (
        proxyErr?.code === 'invalid_credentials' ||
        proxyErr?.code === 'user_not_found' ||
        proxyErr?.code === 'missing_credentials'
      ) {
        throw proxyErr;
      }
      console.warn('[signIn] /api/auth/login proxy fallback to direct client auth:', proxyErr);
    }

    // Direct client fallback
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    if (error) throw error;

    if (data?.user) {
      setUser(data.user);
      setSession(data.session);
      try {
        await ensureUserProfile(supabase, data.user);
        await loadProfile(data.user.id, data.user);
      } catch (err) {
        console.warn('[signIn] loadProfile non-blocking:', err);
      }
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const changePassword = async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
    return data;
  };

  const resendVerificationEmail = async (emailAddress?: string) => {
    const targetEmail = emailAddress || user?.email;
    if (!targetEmail) {
      throw new Error('No email address provided for verification.');
    }
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : process.env.NEXT_PUBLIC_SITE_URL || 'https://avenbeautylink.com';

    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email: targetEmail.trim().toLowerCase(),
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return Boolean(user?.email_confirmed_at);
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) return null;
    return data;
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    changePassword,
    resendVerificationEmail,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
